import React from 'react';
import { ClingConfigPitchGauge } from './ClingConfigPitchGauge';
import {
  VOICE_TESSITURAS,
  getVoiceProfile,
  type VoiceTessitura,
} from '../../lib/melody/clingVoiceConfig';
import type { ClingCharacterDef } from '../../lib/melody/clingCharacterConfig';

interface ClingConfigVoicePanelProps {
  voiceTessitura: VoiceTessitura;
  onVoiceTessitura: (voice: VoiceTessitura) => void;
  character: ClingCharacterDef;
  micReady: boolean;
  micError: string | null;
  simulationMode: boolean;
  livePitch: string | null;
  liveVolume: number;
  liveMidi: number | null;
  micActive: boolean;
  onRequestMic: () => void;
}

function voiceRangeLabel(id: VoiceTessitura): string {
  if (id === 'ninos') return 'Rango de Voz Infantil';
  if (id === 'grave') return 'Rango de Voz Grave';
  return 'Rango de Voz Agudo';
}

export const ClingConfigVoicePanel: React.FC<ClingConfigVoicePanelProps> = ({
  voiceTessitura,
  onVoiceTessitura,
  character,
  micReady,
  micError,
  simulationMode,
  livePitch,
  liveVolume,
  liveMidi,
  micActive,
  onRequestMic,
}) => {
  const volPct = Math.round(Math.max(liveVolume, 0) * 100);
  const profile = getVoiceProfile(voiceTessitura);

  let statusText = profile.description;
  if (!micReady) statusText = 'Activa el micrófono con «Calibrar Tono».';
  else if (simulationMode) statusText = 'Modo teclado (↑ ↓ + espacio)';
  else if (micActive && livePitch) statusText = `Cantando cerca de ${livePitch}`;

  return (
    <section className="cling-config-panel cling-config-panel--voice">
      <h3 className="cling-config-panel-title">
        <span className="cling-config-panel-icon" aria-hidden>
          🎤
        </span>
        Configuración de Voz
      </h3>

      <div className="cling-config-voice-body">
        <div className="cling-config-voice-controls">
          <div className="cling-config-voice-meter-block">
            <div className="cling-config-voice-meter-head">
              <span>Nivel de entrada</span>
              <strong>{micActive ? `${volPct}%` : '—'}</strong>
            </div>
            <div
              className={`cling-config-level-meter ${micActive ? 'cling-config-level-meter--on' : ''}`}
              role="meter"
              aria-valuenow={volPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Nivel de micrófono"
            >
              {Array.from({ length: 12 }).map((_, i) => {
                const threshold = ((i + 1) / 12) * 100;
                const lit = micActive && volPct >= threshold - 6;
                return (
                  <span
                    key={i}
                    className={`cling-config-level-seg ${lit ? 'cling-config-level-seg--lit' : ''}`}
                  />
                );
              })}
            </div>
          </div>

          <div className="cling-config-voice-toolbar">
            <button
              type="button"
              className={`cling-config-btn cling-config-btn--outline ${micReady ? 'cling-config-btn--ready' : ''}`}
              onClick={onRequestMic}
            >
              {micReady ? '↻ Recalibrar' : 'Calibrar Tono'}
            </button>
            <label className="cling-config-select-wrap">
              <span className="cling-config-field-label">Tesitura</span>
              <select
                className="cling-config-select"
                value={voiceTessitura}
                onChange={(e) => onVoiceTessitura(e.target.value as VoiceTessitura)}
              >
                {VOICE_TESSITURAS.map((v) => (
                  <option key={v.id} value={v.id}>
                    {voiceRangeLabel(v.id)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div
            className={`cling-config-detect-badge ${livePitch ? 'cling-config-detect-badge--hit' : micActive ? 'cling-config-detect-badge--listen' : ''}`}
          >
            <span className="cling-config-detect-badge-label">Detectado</span>
            <strong className="cling-config-detect-badge-value">
              {livePitch ?? (micActive ? '…' : '—')}
            </strong>
          </div>

          <p className="cling-config-voice-hint">
            {statusText}
            {micError ? ` · ${micError}` : ''}
          </p>
        </div>

        <ClingConfigPitchGauge
          midi={liveMidi}
          micActive={micActive}
          detectedPitch={livePitch}
          character={character}
        />
      </div>
    </section>
  );
};

export default ClingConfigVoicePanel;
